import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import { useCreateWithdraw } from "@/features/create-withdraw";
import { classify } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { formatMoney } from "@/shared/lib";
import { Button, useToast } from "@/shared/ui";
import { TopBar } from "@/widgets/top-bar";

interface WithdrawDraft {
  amount?: string;
  asset?: string;
  network?: string;
  address?: string;
}

/**
 * Шаг 3: подтверждение. Каркас `Launch Confirmation` (22:207) — DESIGN.md §5.
 *
 * Превышение доступной суммы приходит как 400 `INSUFFICIENT_FUNDS` с
 * `details {available, requested}`. Показываем ровно эти два числа: разницу
 * клиент не считает (правило ТЗ №5). Ради этого случая в политике ошибок и
 * заведена отдельная реакция `insufficient`.
 */
export function WithdrawConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();

  const draft = location.state as WithdrawDraft | null;
  const [shortfall, setShortfall] = useState<{ available?: string; requested?: string } | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const create = useCreateWithdraw();

  if (!draft?.amount || !draft.asset || !draft.network || !draft.address) {
    return <Navigate to={ROUTES.withdrawAmount} replace />;
  }

  const { amount, asset, network, address } = draft;

  const submit = () => {
    setShortfall(null);
    setFieldError(null);
    create.mutate(
      { amount, asset, network, address },
      {
        onSuccess: (request) => {
          void navigate(ROUTES.withdrawal(request.id), { replace: true });
        },
        onError: (error) => {
          const reaction = classify(error);
          if (reaction.kind === "insufficient") {
            setShortfall({ available: reaction.available, requested: reaction.requested });
          } else if (reaction.kind === "inline") {
            setFieldError(reaction.message);
          } else if (reaction.kind === "conflict") {
            toast.show({ tone: "loss", title: reaction.message });
          } else if (reaction.kind !== "offline") {
            toast.show({ tone: "loss", title: t("states.error.title") });
          }
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("withdraw.confirmTitle")} subtitle={t("withdraw.confirmSubtitle")} />

      <dl className="flex flex-col gap-3 rounded-3xl border border-border-subtle bg-background-elevated p-5">
        <div className="flex items-start justify-between gap-3">
          <dt className="text-caption font-medium text-text-secondary">{t("withdraw.amount")}</dt>
          <dd className="text-label font-semibold text-text-primary">
            {formatMoney(amount, { suffix: asset })}
          </dd>
        </div>
        <div className="flex items-start justify-between gap-3">
          <dt className="text-caption font-medium text-text-secondary">{t("wallet.network")}</dt>
          <dd className="text-label font-semibold text-text-primary">{network}</dd>
        </div>
        <div className="flex items-start justify-between gap-3">
          <dt className="shrink-0 text-caption font-medium text-text-secondary">
            {t("withdraw.addressLabel")}
          </dt>
          <dd className="min-w-0 font-mono text-caption break-all text-text-primary">{address}</dd>
        </div>
      </dl>

      <p className="rounded-2xl border border-border-subtle bg-background-elevated p-4 text-caption font-normal text-text-secondary">
        {t("withdraw.manualNote")}
      </p>

      {shortfall ? (
        <p role="alert" className="rounded-2xl border border-border-loss bg-surface-loss p-4 text-caption font-normal text-text-primary">
          {t("withdraw.insufficient", {
            requested: formatMoney(shortfall.requested ?? amount, { suffix: "USDT" }),
            available: formatMoney(shortfall.available ?? "0.00", { suffix: "USDT" }),
          })}
        </p>
      ) : null}

      {fieldError ? (
        <p role="alert" className="text-caption font-medium text-status-loss">
          {fieldError}
        </p>
      ) : null}

      {/* `commit`, а не обычный отклик CTA: отсюда деньги уходят со счёта
          (FE-14). */}
      <Button size="cta" block haptic="commit" loading={create.isPending} onClick={submit}>
        {t("withdraw.submit")}
      </Button>
    </div>
  );
}
